import { Dialog } from '@headlessui/react';
import { useDialog, useTheme } from '@ecdlink/core';
import { IonContent } from '@ionic/react';
import { ActionModal, BannerWrapper, DialogPosition } from '@ecdlink/ui';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { userSelectors } from '@store/user';
import { AddPhoto } from './components/add-photo/add-photo';
import {
  EditPractitionerSteps,
  PractitionerFormData,
} from './edit-practitioner-profile.types';
import { useOnlineStatus } from '@hooks/useOnlineStatus';
import OnlineOnlyModal from '../../../modals/offline-sync/online-only-modal';
import { authSelectors } from '@/store/auth';
import { PractitionerSetup } from './components/practitioner-setup/practitioner-setup';
import { WelcomePage } from '@/components/welcome-page';
import { PractitionerService } from '@/services/PractitionerService';
import {
  practitionerSelectors,
  practitionerThunkActions,
} from '@/store/practitioner';
import ROUTES from '@/routes/routes';
import { useAppDispatch } from '@store';
import {
  notificationActions,
  notificationsSelectors,
} from '@/store/notifications';
import { useNotificationService } from '@/hooks/useNotificationService';
import { PractitionerSignature } from './components/practitioner-signature/practitioner-signature';
import { disableBackendNotification } from '@/store/notifications/notifications.actions';
import { InviteService } from '@/services/InviteService';
import { InviteDto } from '@ecdlink/core/src/models/dto/Invite/invite.dto';

export const EditPractitionerProfile: React.FC = () => {
  const appDispatch = useAppDispatch();
  const history = useHistory();
  const { theme } = useTheme();
  const dialog = useDialog();
  const { isOnline } = useOnlineStatus();

  const userAuth = useSelector(authSelectors.getAuthUser);
  const user = useSelector(userSelectors.getUser);
  const practitioner = useSelector(practitionerSelectors.getPractitioner);

  const [label, setLabel] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(EditPractitionerSteps.WELCOME);
  const [formData, setFormData] = useState<PractitionerFormData>({
    practitionerToProgramme: false,
    allowPermissions: false,
    maxChildren: 0,
  });

  const addedByPrincipal = !!practitioner?.principalHierarchy;

  let invite: InviteDto | null;

  const { stopService } = useNotificationService();

  const showOnlineOnly = useCallback(() => {
    dialog({
      position: DialogPosition.Middle,
      render: (onSubmit) => {
        return (
          <OnlineOnlyModal
            overrideText={'You need to be online to complete your profile'}
            onSubmit={() => {
              history.goBack();
              onSubmit();
            }}
          ></OnlineOnlyModal>
        );
      },
    });
  }, [dialog, history]);

  const showCompleteProfileFailed = useCallback(() => {
    dialog({
      position: DialogPosition.Middle,
      render: (onSubmit) => (
        <Dialog.Panel>
          <ActionModal
            icon={'XCircleIcon'}
            iconColor={'alertMain'}
            iconBorderColor="alertBg"
            title={'Something went wrong'}
            detailText={
              'We could not finish setting up your profile. Please try again.'
            }
            actionButtons={[
              {
                colour: 'primary',
                text: 'Close',
                onClick: () => onSubmit(),
                textColour: 'white',
                type: 'filled',
                leadingIcon: 'XIcon',
              },
            ]}
          />
        </Dialog.Panel>
      ),
    });
  }, [dialog]);

  useEffect(() => {
    if (!isOnline) {
      showOnlineOnly();
    }
  }, [isOnline, showOnlineOnly]);

  useEffect(() => {
    const checkInvite = async () => {
      try {
        const inviteService = new InviteService(userAuth?.auth_token!);
        invite = await inviteService.getInviteByPractitionerId(user?.id!);

        if (!addedByPrincipal && isOnline && !invite) {
          history.push(ROUTES.PRINCIPAL.SETUP_PROFILE);
        }
      } catch (error) {
        console.error('Error checking invite:', error);
      }
    };

    checkInvite();
  }, [addedByPrincipal, history, isOnline, user?.id]);

  useEffect(() => {
    if (activeStep === EditPractitionerSteps.WELCOME) {
      setLabel('Welcome');
    } else {
      setLabel(
        `step ${activeStep} of ${
          Object.values(EditPractitionerSteps).filter(Number).length
        }`
      );
    }
  }, [activeStep]);

  const principalChangedNotifications = useSelector(
    notificationsSelectors.getAllNotifications
  ).filter(
    (item) =>
      (item?.message?.cta?.includes('[[PrincipalAgreement]]') &&
        item?.message?.action?.includes('practitioner/profile/edit')) ||
      item?.message?.actionText
        ?.toLocaleLowerCase()
        ?.includes('update your profile') ||
      item?.message?.actionText?.toLocaleLowerCase()?.includes('edit profile')
  );

  const removeNotifications = async () => {
    if (
      principalChangedNotifications &&
      principalChangedNotifications?.length > 0
    ) {
      principalChangedNotifications.map((notification) => {
        appDispatch(
          disableBackendNotification({
            notificationId: notification.message.reference ?? '',
          })
        );
      });
    }
  };

  const onAllStepsComplete = async () => {
    if (!isOnline) {
      return showOnlineOnly();
    }

    // A practitioner invited by a principal only gets linked to that principal
    // on the server, so the cached practitioner has no principalHierarchy while
    // this flow runs. Gating on it here left the last step with nothing to do.
    if (!userAuth?.auth_token || !user?.id) {
      return showCompleteProfileFailed();
    }

    setIsSubmitting(true);

    try {
      if (formData.allowPermissions) {
        removeNotifications();

        const practitionerService = new PractitionerService(
          userAuth.auth_token
        );

        // explicitly checking that the user concent to share info
        await practitionerService.UpdatePractitionerShareInfo(user.id);
        await practitionerService.UpdatePractitionerRegistered(user.id, true);
        await practitionerService.UpdatePractitionerProgress(user.id, 2.0);
        await practitionerService.UpdatePractitionerMaxChildren(
          user.id,
          Number(formData.maxChildren)
        );
      }

      appDispatch(notificationActions.resetFrontendNotificationState());

      await appDispatch(
        practitionerThunkActions.getPractitionerByUserId({
          userId: user.id,
        })
      );
      await appDispatch(practitionerThunkActions.getAllPractitioners({}));

      stopService();
      history.push(ROUTES.ROOT);
    } catch (error) {
      console.error('Error completing practitioner profile:', error);
      showCompleteProfileFailed();
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = (step: EditPractitionerSteps) => {
    switch (step) {
      case EditPractitionerSteps.SETUP_PRACTITIONER:
        return (
          <PractitionerSetup
            onSubmit={(form: PractitionerFormData) => {
              setFormData(form);
              setActiveStep(EditPractitionerSteps.ADD_SIGNATURE);
            }}
          />
        );

      case EditPractitionerSteps.ADD_SIGNATURE:
        return (
          <PractitionerSignature
            onSubmit={(e) => setActiveStep(EditPractitionerSteps.ADD_PHOTO)}
          />
        );

      case EditPractitionerSteps.ADD_PHOTO:
        return (
          <AddPhoto
            onSubmit={() => {
              onAllStepsComplete();
            }}
            isLoading={isSubmitting}
          />
        );

      case EditPractitionerSteps.WELCOME:
      default:
        return (
          <WelcomePage
            onNext={() =>
              setActiveStep(EditPractitionerSteps.SETUP_PRACTITIONER)
            }
          />
        );
    }
  };

  const exitPrompt = () => {
    dialog({
      position: DialogPosition.Bottom,
      render: (onSubmit, onCancel) => (
        <Dialog.Panel>
          <ActionModal
            icon={'XCircleIcon'}
            iconColor={'alertMain'}
            iconBorderColor="alertBg"
            importantText={
              'Please complete the process otherwise you will lose your changes.'
            }
            actionButtons={[
              {
                colour: 'primary',
                text: 'Exit',
                onClick: () => {
                  onSubmit();
                  history.goBack();
                },
                textColour: 'white',
                type: 'filled',
                leadingIcon: 'LoginIcon',
              },
              {
                colour: 'primary',
                text: 'Continue editing',
                onClick: () => {
                  onCancel();
                },
                textColour: 'primary',
                type: 'outlined',
                leadingIcon: 'PencilIcon',
              },
            ]}
          />
        </Dialog.Panel>
      ),
    });
  };

  const onBack = () => {
    switch (activeStep) {
      case EditPractitionerSteps.WELCOME:
      default:
        return history.goBack();
      case EditPractitionerSteps.SETUP_PRACTITIONER:
        return setActiveStep(EditPractitionerSteps.WELCOME);
      case EditPractitionerSteps.ADD_SIGNATURE:
        return setActiveStep(EditPractitionerSteps.SETUP_PRACTITIONER);
      case EditPractitionerSteps.ADD_PHOTO:
        return setActiveStep(EditPractitionerSteps.ADD_SIGNATURE);
    }
  };

  return (
    <>
      <IonContent scrollY={true}>
        <BannerWrapper
          size={
            activeStep === EditPractitionerSteps.WELCOME ? 'large' : 'medium'
          }
          renderBorder={true}
          showBackground={activeStep === EditPractitionerSteps.WELCOME}
          title={'Edit Profile'}
          subTitle={label}
          onBack={onBack}
          onClose={exitPrompt}
          backgroundColour={'white'}
          className={
            activeStep === EditPractitionerSteps.WELCOME ? 'relative' : ''
          }
          backgroundUrl={
            activeStep === EditPractitionerSteps.WELCOME
              ? theme?.images.graphicOverlayUrl
              : ''
          }
          displayOffline={!isOnline}
        >
          <div className={'px-4'}>{steps(activeStep)}</div>
        </BannerWrapper>
      </IonContent>
    </>
  );
};
